import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { com } from '@eidu/form'
import { Box, useTheme } from '@mui/material'
import ReactCodeMirror, { EditorView, ReactCodeMirrorProps, highlightWhitespace } from '@uiw/react-codemirror'
import { createTheme } from '@uiw/codemirror-themes'
import { tags } from '@lezer/highlight'
import type { JSONSchema7 } from 'json-schema'
import { yamlSchema } from 'codemirror-json-schema/yaml'
import { jsonSchema as jsonSchemaExt } from 'codemirror-json-schema'
import { getLanguageExtensions, LanguageSelect, useLanguageDetection } from './LanguageSelect'
import {
  AssetTriggerRange,
  assetReferenceDecorationPlugin,
  configureAssetDecorations,
  initialAssetDecorationExtensions,
} from './assetReferenceDecoration'
import { createAssetChipTheme } from './assetChipTheme'
import useAssetReferenceLabelResolver from './useAssetReferenceLabelResolver'
import { assetTriggerExtension } from './assetTriggerExtension'
import AssetReferencePicker from './AssetReferencePicker'
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import EntityId = com.eidu.sharedlib.entity.EntityId

type PayloadEditorProps = {
  value?: string
  onChange?: (value: string) => void
  editable?: boolean
  placeholder?: string
  jsonSchema?: Record<string, unknown>
}

const PayloadEditor = ({ value, onChange, editable = true, placeholder, jsonSchema }: PayloadEditorProps) => {
  const { resolvedLabels: resolvedAssetLabels } = useAssetReferenceLabelResolver(value ?? '')
  const muiTheme = useTheme()
  const { activeLanguage, selectActiveLanguage } = useLanguageDetection(value)

  const [assetTriggerRange, setAssetTriggerRange] = useState<AssetTriggerRange | undefined>(undefined)
  const [pickerEntityId, setPickerEntityId] = useState<EntityId | undefined>(undefined)

  const cmTheme = useMemo(
    () =>
      createTheme({
        theme: 'light',
        settings: {
          background: muiTheme.palette.background.paper,
          foreground: muiTheme.palette.text.primary,
          selection: muiTheme.palette.action.selected,
          gutterBackground: muiTheme.palette.grey[50],
          gutterForeground: muiTheme.palette.text.disabled,
          lineHighlight: muiTheme.palette.action.hover,
        },
        styles: [
          { tag: tags.keyword, color: muiTheme.palette.primary.main },
          { tag: tags.string, color: muiTheme.palette.success.dark },
          { tag: tags.number, color: muiTheme.palette.warning.dark },
          { tag: tags.comment, color: muiTheme.palette.text.disabled },
          { tag: tags.propertyName, color: muiTheme.palette.info.dark },
        ],
      }),
    [muiTheme]
  )

  const whitespaceStyle = useMemo(
    () =>
      EditorView.theme({
        '.cm-highlightSpace': {
          backgroundImage: 'radial-gradient(circle at 50% 55%, #f5f5f5 20%, transparent 5%)',
        },
        '.cm-highlightTab': {
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="20"><path stroke="%23f5f5f5" stroke-width="1" fill="none" d="M1 10H196L190 5M190 15L196 10M197 4L197 16"/></svg>')`,
        },
        '.cm-activeLine .cm-highlightSpace': {
          backgroundImage: 'radial-gradient(circle at 50% 55%, white 20%, transparent 5%)',
        },
        '.cm-activeLine .cm-highlightTab': {
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="20"><path stroke="white" stroke-width="1" fill="none" d="M1 10H196L190 5M190 15L196 10M197 4L197 16"/></svg>')`,
        },
        '.cm-selectionBackground .cm-highlightSpace': {
          backgroundImage: 'radial-gradient(circle at 50% 55%, white 20%, transparent 5%)',
        },
        '.cm-selectionBackground .cm-highlightTab': {
          backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="20"><path stroke="white" stroke-width="1" fill="none" d="M1 10H196L190 5M190 15L196 10M197 4L197 16"/></svg>')`,
        },
      }),
    [muiTheme]
  )

  const assetChipStyle = useMemo(() => createAssetChipTheme(muiTheme), [muiTheme])

  const viewRef = useRef<EditorView | null>(null)

  const onAssetTrigger = useCallback((range: AssetTriggerRange) => {
    setAssetTriggerRange(range)
    const view = viewRef.current
    if (view) {
      const text = view.state.doc.sliceString(range.from, range.to)
      const match = text.match(/^asset:\/\/(.+)$/)
      setPickerEntityId(match ? entityIdFromString(match[1]) ?? undefined : undefined)
    } else {
      setPickerEntityId(undefined)
    }
  }, [])

  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    configureAssetDecorations(view, {
      labels: resolvedAssetLabels ?? new Map(),
      editable,
      onEditAsset: onAssetTrigger,
    })
  }, [resolvedAssetLabels, editable, onAssetTrigger])

  const schemaExtensions = useMemo(() => {
    if (!jsonSchema) return []
    const schema = jsonSchema as JSONSchema7
    if (activeLanguage === 'yaml') return yamlSchema(schema)
    if (activeLanguage === 'json') return jsonSchemaExt(schema)
    return []
  }, [jsonSchema, activeLanguage])

  const extensions = useMemo(
    () => [
      ...(schemaExtensions.length > 0 ? [] : getLanguageExtensions(activeLanguage)),
      ...schemaExtensions,
      EditorView.lineWrapping,
      highlightWhitespace(),
      whitespaceStyle,
      assetReferenceDecorationPlugin(),
      assetChipStyle,
      ...initialAssetDecorationExtensions({
        labels: resolvedAssetLabels ?? new Map(),
        editable,
        onEditAsset: onAssetTrigger,
      }),
      ...(editable ? [assetTriggerExtension] : []),
    ],
    [activeLanguage, whitespaceStyle, schemaExtensions]
  )

  const handlePickerConfirm = useCallback(() => {
    const view = viewRef.current
    if (!view || !assetTriggerRange || !pickerEntityId) return
    const replacement = `asset://${pickerEntityId.toString()}`
    view.dispatch({ changes: { from: assetTriggerRange.from, to: assetTriggerRange.to, insert: replacement } })
    setAssetTriggerRange(undefined)
    setPickerEntityId(undefined)
  }, [assetTriggerRange, pickerEntityId])

  const handlePickerCancel = useCallback(() => {
    setAssetTriggerRange(undefined)
    setPickerEntityId(undefined)
  }, [])

  const props: ReactCodeMirrorProps = {
    value,
    onChange,
    editable,
    placeholder,
    height: '100%',
    theme: cmTheme,
    extensions,
    basicSetup: {
      lineNumbers: true,
      foldGutter: true,
      highlightActiveLine: editable,
    },
    style: { flex: 1, minHeight: 0, overflow: 'auto' },
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '&:focus-within': editable
          ? { borderColor: 'primary.main', boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}` }
          : undefined,
      }}
    >
      <LanguageSelect value={activeLanguage} onChange={selectActiveLanguage} />
      <ReactCodeMirror
        {...props}
        onCreateEditor={(view) => {
          viewRef.current = view
        }}
      />
      {assetTriggerRange && (
        <AssetReferencePicker
          open
          value={pickerEntityId}
          onChange={setPickerEntityId}
          onConfirm={handlePickerConfirm}
          onCancel={handlePickerCancel}
        />
      )}
    </Box>
  )
}

export default PayloadEditor
